import interpolate from 'lib/interpolate';
import invariant from 'fbjs/lib/invariant.js';
import { RequestError } from '#/lib/errors/errors.js';
import { rewireConfig } from '#/services/rewire-config.js';

/**
 * @class InteractiveAPI
 * @description Class for fetching data from the API
 * @param {Object} configProvider - observable config holder
 * @param {(error) => {handled: boolean, retry: boolean, queue: boolean}} errorHnadler - callbacks for handling errors
 */
export default class InteractiveApi {
  #pendingQuery = [];
  /**
   * @type {(error: RequestError) => {handled: boolean, retry: boolean, queue: boolean}}
   */
  #errorHandler;
  #token;
  #coreToken;
  #baseUrl;
  #getConfigUrl;
  #configVersion;

  constructor(configProvider, errorHandler) {
    invariant(configProvider, 'configProvider is required');

    configProvider.subscribe(async (config) => {
      this.#token = config.token;
      this.#baseUrl = config.api_config.url;
      this.#getConfigUrl = interpolate.bind(
        this,
        config.api_config.video_config_url,
      );
      this.#configVersion = config.api_config.version;
      // this.#coreToken = await this.#getCoreToken(config.token);

      this.#pendingQuery.forEach((callback) => callback());
      this.#pendingQuery.length = 0;
    });
    this.#errorHandler = errorHandler;
    // this.#configPattern = video_config_url.replace(api_url, '');
  }

  debug = true;
  get #Authorization() {
/*
    if (this.debug) {
      this.debug = false;
      return `Bearer ${this.#token}_broken`;
    } else {
      this.debug = true;
      return `Bearer ${this.#token}`;
    }
*/
    return `Bearer ${this.#token}`;
  }

  query(path, retryCount = 0) {
    const url = this.#baseUrl + path;
    // request.headers.set('Cache-control', 'max-age=300' /* 'no-cache' */);
    const request = new Request(url);
    request.headers.set('Authorization', this.#Authorization);
    request.headers.set('Accept', 'application/json');

    return fetch(request)
      .then(this.#parseResponse, () => this.#onRejectedRequest(url))
      .then((payload) => this.#checkResponsePayload(payload, url))
      .catch((error) => this.#handleRequestError(error, path))
      .catch((error) => {
        const { allowRetry, delay } = this.#internalErrorHandler(
          error,
          retryCount,
        );

        if (allowRetry) {
          console.warn(`Retry request in ${delay} ms`);
          return new Promise((resolve) => setTimeout(resolve, delay)).then(() =>
            this.query(path, retryCount + 1),
          );
        }

        throw error;
      });
  }

  post(path, contentType = 'json', body) {
    const url = this.#baseUrl + path;
    const request = new Request(url, { method: 'POST', body });

    request.headers.set('Authorization', this.#Authorization);
    request.headers.set('Accept', `application/${contentType}`);

    return fetch(request)
      .then(this.#parseResponse, (error) => this.#onRejectedRequest(error, url))
      .then((payload) => this.#checkResponsePayload(payload, url))
      .catch((error) => this.#handleRequestError(error, path))
  }

  japiQuery(path, params = {}) {
    invariant(this.#coreToken, 'authorization is required');

    const search = new URLSearchParams({ access_token: this.#coreToken, ...params });
    return this.query(`/japi-core/proxy/${path}?${search.toString()}`);
  }

  japiPost(path, payload = {}) {
    invariant(this.#coreToken, 'authorization is required');

    const body = new URLSearchParams({ access_token: this.#coreToken, ...payload });
    return this.post(`/japi-core/proxy/${path}`, 'x-www-form-urlencoded', body);
  }

  init() {
    console.warn('Unexpected usage');
    return this.post('/v1/init');
  }

  async getConfig(object) {
      invariant(
        object?.video_id,
        'Get config failure: Missing required property `video_id`',
      );
      invariant(
        object?.content_type,
        'Get config failure: Missing required property `content_type` for video_id: %s',
        object.video_id
      );

    const url = this.#getConfigUrl(object);
    return this.query(url)
      .then((config) => {
      config._version = this.#configVersion;

        // TODO: remove after BE updates config for quiz
        config.quiz ??= {
          enabled: true,
        };
        // TODO: remove manual patching
        config['debug-layer'] ??= {
          enabled: true,
        };

      console.debug('config fetched', {
        object,
        config,
      });

      // TODO: move here all other config "modifications" from fetchObjectConfig,
      //  except replacing video_id in templates
      return config;
    })
      .then(config => rewireConfig(config, object))
      .then(config => {
        // rename backend preferred name to required "as same as extension" name

        config['promo-landing'] = config['promo'];
        delete config['promo']; // ????

        // config['drop-notification'] = config['gifts'];
        delete config['gifts'];


        if (config._version === '3' && config.poll?.enabled) {
          console.warn("apply poll's config patch");
          /* patch v3 config to match v4,
           * delete after transferring all clients to version 4 */
          config.poll.subscribe = {
            event: '/v1/video/{{video_id}}/device/{{device_id}}',
            result: config['poll'].subscribe,
          };
          config.poll.status_api = '/v3/poll/{{poll_id}}/voted';
        }

        return config;

      });
  }

  /*
      updateToken(newToken) {
        authorization = `Bearer ${newToken}`;
        pendingQuery.forEach((callback) => callback());
        pendingQuery.length = 0;
      },
  */

  #internalErrorHandler = (error, retryCount) => {
    let allowRetry = retryCount < 3;
    let delay = 5_000;

    switch (true) {
      case error.status === 429:
      case error.status >= 500: {
        /* any server fails AND client's 900, see above */
        allowRetry &&= true;

        const retryHeader = error.headers?.get('Retry-After');
        if (retryHeader && typeof retryHeader === 'number') {
          delay = retryHeader * 1000;
        }
        break;
      }
      default:
        console.log('Disable retry', error);
        allowRetry = false;
    }

    return {
      allowRetry,
      delay,
    };
  }

  #getCoreToken(token) {
    invariant(token, 'get token failure');

    return this.post(`/core/token/${token}`)
      .then((data) => data.tokens.access_token)
      .catch((error) => {
        console.warn('getting token error', error);
        return null;
      });
  }

  #handleRequestError(error, path) {
    const handling = this.#errorHandler(error);

    if (!handling.handled) {
      throw error;
    }

    if (handling.queue) {
      return new Promise((resolve) => {
        this.#pendingQuery.push(() => resolve(this.query(path)));
      });
    }
  }

  #parseResponse(response) {
    if (response.ok) {
      return response.json();
    }

    console.warn('Catch error in response status');
    throw new RequestError(response);
  }

  #checkResponsePayload(payload, url) {
    const { result, data } = payload;

    if (result === 'ok') {
      return data;
    }

    console.warn('Catch error in response data');
    const { message: statusText, code: status } = payload;
    throw new RequestError({ status, statusText, url });
  }

  #onRejectedRequest(url) {
    console.warn('Catch error on request attempt');
    throw new RequestError({
      status: 900,
      statusText: 'API request failure',
      url,
    });
  }
}
